const BigNumber = require('bignumber.js');
const Payment = require('@/lib/Payments/Payment');
const PaymentMethodRef = require('@/lib/PaymentMethods/PaymentMethodRef');
const UserFacingError = require('@/libraries/DigiTickets/Errors/UserFacingError');
const { sumTotalOfLinesByItemID } = require('@/lib/Lines/lineCalculations');

/**
 * This class deals with generating payments for Clubcard vouchers, and ensuring we create Clubcard voucher payments
 * for the correct amounts.
 *
 * The customer has converted their Clubcard points into one or more tokens/vouchers for a DigiTickets client.
 * - Each token/voucher has a list of items it can be redeemed for.
 * - You can redeem more than voucher, even against the same ticket. e.g. If you have a £10 ticket you can use 2x £5
 *   vouchers to pay for that one ticket.
 * - You can also use 1x £20 voucher to pay for 2x £10 tickets.
 * - The balance of vouchers should only apply to valid tickets. e.g. If the cart has a £7 ticket and a £3 snack, and
 *   the customer has a £10 voucher, they still need to pay £3 for the snack - the voucher only pays for the ticket.
 */
class ClubcardTokenApplicator {
    /**
     * @param {CartService} cartService
     * @param {PaymentMethodService} paymentMethodService
     */
    constructor(
        cartService,
        paymentMethodService
    ) {
        this.cartService = cartService;
        this.paymentMethodService = paymentMethodService;
    }

    /**
     * Creates a Payment from a ClubcardTokenWithItems.
     * This does some calculating and validating and it may throw an Error if a Payment cannot be created.
     * - Ensures this token is not already in the cart.
     * - Ensures there is some remaining balance in the cart that can have a voucher used for it, based on the
     *   items a voucher is valid for.
     *
     * @param {ClubcardTokenWithItems} tokenWithItems
     *
     * @return {Payment}
     * @throws {UserFacingError}
     */
    generatePaymentForToken(tokenWithItems) {
        const cart = this.cartService.currentCart;

        const token = tokenWithItems.token;

        // Prevent adding the same token to the cart twice.
        this.checkForDuplicateToken(token, cart);

        // Check for existing Tesco payments in the cart.
        const existingPayments = this.getExistingClubcardPayments(cart);
        // Extract Tesco tokens from those existing payments.
        const existingTokensWithItems = existingPayments.map((p) => p.clubcardTokenWithItems);

        // Build a list of all tokens, including the one we are adding.
        const proposedTokens = [
            ...existingTokensWithItems,
            tokenWithItems,
        ];

        // Calculate the max amount that can be paid from these tokens for what is in the cart.
        const totalValueOfApplicableItems = this.getTotalOfApplicableItemsInCart(proposedTokens, cart);
        if (totalValueOfApplicableItems <= 0) {
            throw new UserFacingError(
                `Total value of applicable items in the cart is ${totalValueOfApplicableItems}`,
                'PAYMENT.NO_BALANCE_FOR_VOUCHER'
            );
        }

        // Add up how much has already been paid by voucher.
        let existingVoucherPaymentTotal = new BigNumber(0);
        existingPayments.forEach((p) => {
            existingVoucherPaymentTotal = existingVoucherPaymentTotal.plus(p.tendered);
        });

        // Calculate how much can still be paid by voucher - total payable by voucher minus what is
        // already paid by voucher.
        // Note that this does not take into account any non-Tesco-voucher payments in the cart. But that seems
        // sensible - if a customer pays £10 then realises they have a coupon they should be able to still add it.
        // Although they would need their £10 refunding - that's a different issue!
        const remainingBalancePayableByVoucher = new BigNumber(totalValueOfApplicableItems)
            .minus(existingVoucherPaymentTotal)
            .toNumber();

        if (remainingBalancePayableByVoucher <= 0) {
            throw new UserFacingError(
                `Total remaining value of applicable items in the cart is ${remainingBalancePayableByVoucher}`,
                'PAYMENT.NO_REMAINING_BALANCE_FOR_VOUCHER'
            );
        }

        // Generate a new payment with an amount that is the lesser of this new tokens' value,
        // or the amount that can still be paid by voucher.
        let paymentAmount = Math.min(
            token.value,
            remainingBalancePayableByVoucher
        );

        // Ensure the voucher payment amount does not exceed the balance remaining in the cart.
        paymentAmount = Math.min(
            paymentAmount,
            this.cartService.getRemainingBalance()
        );

        if (paymentAmount <= 0) {
            throw new UserFacingError(
                `Voucher payment amount would be ${paymentAmount}`,
                'PAYMENT.NO_REMAINING_BALANCE_FOR_VOUCHER'
            );
        }

        return this.createPayment(tokenWithItems, paymentAmount);
    }

    /**
     * @param {Cart} cart
     * @return {Payment[]}
     */
    getExistingClubcardPayments(cart) {
        return cart.payments.filter((p) => p.paymentMethod?.ref === PaymentMethodRef.TESCO_VOUCHER_UK);
    }

    /**
     * Each voucher has a list of items it is valid for.
     * This function calculates the total value in the cart for all items that the given token(s) are valid for.
     * e.g. If you have 2x Item A (£10) and 1x Item B (£5) in the cart,
     * and you have a 1x voucher valid for items A and B, and 1x voucher valid for item A, it will return:
     * (2 * £10) + (1 & £5) = £25
     * Don't worry that this total may be greater than the value of all the vouchers - that gets handled later.
     *
     * We are working on the basis that a token is just a balance and that balance can apply to more than one item.
     * It is not a "1 token = 1 ticket" situation like experience vouchers. The difficulty is just that this balance
     * can only apply to certain items.
     *
     * Note that when calculating the total prices we are only considering newly added item to the cart.
     *
     * @param {ClubcardTokenWithItems[]} tokensWithItems
     * @param {Cart} cart
     *
     * @return {number}
     */
    getTotalOfApplicableItemsInCart(tokensWithItems, cart) {
        const applicableCartLines = [];

        // For each voucher we are going to find lines that this voucher can apply to and add them
        // to applicableLinesInCart.
        tokensWithItems.forEach((tokenWithItems) => {
            tokenWithItems.items.forEach((item) => {
                const cartLinesWithItem = cart.getLinesByItemID(item.ID);
                cartLinesWithItem.forEach((line) => {
                    if (!applicableCartLines.includes(line)) {
                        applicableCartLines.push(line);
                    }
                });
            });
        });

        const lineTotalsByItemID = sumTotalOfLinesByItemID(
            Object.values(applicableCartLines),
            /**
             * @param {CartLine} line
             * @return {number}
             */
            (line) => line.getLineTotal()
        );

        let total = new BigNumber(0);
        Object.values(lineTotalsByItemID).forEach((itemTotal) => {
            total = total.plus(itemTotal);
        });

        return total.toNumber();
    }

    /**
     * @param {ClubcardToken} token
     * @param {Cart} cart
     */
    checkForDuplicateToken(token, cart) {
        // Check if there is an existing payment in the cart for the same token.
        const existingTokenPayment = cart.payments.find(
            (p) => p.paymentMethod?.ref === PaymentMethodRef.TESCO_VOUCHER_UK
                && p.transactionRef === token.code
        );
        if (existingTokenPayment) {
            throw new UserFacingError(`Duplicate voucher code ${token.code}`, 'PAYMENT.DUPLICATE_VOUCHER');
        }
    }

    /**
     * @param {ClubcardTokenWithItems} tokenWithItems
     * @param {number} amount
     *
     * @return {Payment}
     */
    createPayment(tokenWithItems, amount) {
        const payment = new Payment();
        payment.setPaymentMethod(this.getPaymentMethod());

        // We don't set thirdPartyID on the payment yet.

        // Transaction ref of the payment is the voucher code.
        payment.transactionRef = tokenWithItems.token.code;

        // The payment amount will be the minimum of either the token's value or the item's price.
        payment.setTendered(amount);

        // Set the token on the Payment so we can refer back to it later.
        payment.clubcardTokenWithItems = tokenWithItems;

        // When we send the payment to the API it will call the Clubcard API to redeem the voucher.
        // At that point the statusDetail will be set as a JSON string with the Clubcard API response.

        return payment;
    }

    /**
     * @return {PaymentMethod}
     */
    getPaymentMethod() {
        return this.paymentMethodService.getByRef(
            PaymentMethodRef.TESCO_VOUCHER_UK
        );
    }
}

module.exports = ClubcardTokenApplicator;
